import { useMemo } from 'react';
import { useEmpresa } from '../contexts/EmpresaContext';

// ── Kzero Consultoria ──────────────────────────────────────────────────────
import kzeroConfig          from '../data/empresas/kzero-consultoria/config.json';
import kzeroPlano           from '../data/empresas/kzero-consultoria/plano-contas.json';
import kzeroRegras          from '../data/empresas/kzero-consultoria/regras-classificacao.json';
import kzeroReceitasPgdas   from '../data/empresas/kzero-consultoria/receitas-pgdas.json';
import kzeroDadosReferencia from '../data/empresas/kzero-consultoria/dados-referencia.json';
import kzeroAbertura2026    from '../data/empresas/kzero-consultoria/abertura-2026.json';
import kzeroLanc2025        from '../data/empresas/kzero-consultoria/lanc-2025.json';
import kzeroLancQ1_2026     from '../data/empresas/kzero-consultoria/lanc-q1-2026.json';

// ── KzeroSystem (empresa nova, ainda vazia) ────────────────────────────────
import kzsConfig            from '../data/empresas/kzerosystem/config.json';
import kzsPlano             from '../data/empresas/kzerosystem/plano-contas.json';
import kzsRegras            from '../data/empresas/kzerosystem/regras-classificacao.json';
import kzsReceitasPgdas     from '../data/empresas/kzerosystem/receitas-pgdas.json';
import kzsDadosReferencia   from '../data/empresas/kzerosystem/dados-referencia.json';
import kzsAbertura2026      from '../data/empresas/kzerosystem/abertura-2026.json';
import kzsLanc2026          from '../data/empresas/kzerosystem/lanc-2026.json';

// ── ADC INNOVATION LTDA (cliente — Bianca é a contadora) ───────────────────
import adcConfig            from '../data/empresas/adc-innovation/config.json';
import adcPlano             from '../data/empresas/adc-innovation/plano-contas.json';
import adcRegras            from '../data/empresas/adc-innovation/regras-classificacao.json';
import adcReceitasPgdas     from '../data/empresas/adc-innovation/receitas-pgdas.json';
import adcDadosReferencia   from '../data/empresas/adc-innovation/dados-referencia.json';
import adcAbertura2026      from '../data/empresas/adc-innovation/abertura-2026.json';
import adcLanc2025          from '../data/empresas/adc-innovation/lanc-2025.json';
import adcLanc2026          from '../data/empresas/adc-innovation/lanc-2026.json';

// ── LOUSI VELLER BIOMEDICINA ESTÉTICA LTDA (cliente — Bianca é a contadora) ──
import lousiConfig          from '../data/empresas/lousi-veller/config.json';
import lousiPlano           from '../data/empresas/lousi-veller/plano-contas.json';
import lousiRegras          from '../data/empresas/lousi-veller/regras-classificacao.json';
import lousiReceitasPgdas   from '../data/empresas/lousi-veller/receitas-pgdas.json';
import lousiDadosReferencia from '../data/empresas/lousi-veller/dados-referencia.json';
import lousiAbertura2026    from '../data/empresas/lousi-veller/abertura-2026.json';
import lousiLanc2026        from '../data/empresas/lousi-veller/lanc-2026.json';

/**
 * Registry dos datasets contábeis indexado por slug da empresa.
 *
 * Cada entrada segue o shape:
 *  - config:           objeto JSON do config.json
 *  - planoContas:      array de contas
 *  - regrasClassif:    array de regras { padrao, contaContabil }
 *  - receitasPgdas:    objeto { 'AAAA-MM': valor }
 *  - dadosReferencia:  objeto { [ano]: { dre, bp, dlpa, ... } }
 *  - aberturaPorAno:   objeto { [ano]: [lançamentos de abertura] }
 *  - lancamentosPorAno:objeto { [ano]: [lançamentos] | [lançamentos do trimestre] }
 *
 * Para adicionar uma nova empresa: criar a pasta com os 7 JSONs e adicionar entry aqui.
 */
const REGISTRY = {
  'kzero-consultoria': {
    config: kzeroConfig,
    planoContas: kzeroPlano,
    regrasClassif: kzeroRegras,
    receitasPgdas: kzeroReceitasPgdas,
    dadosReferencia: kzeroDadosReferencia,
    aberturaPorAno: { '2026': kzeroAbertura2026 },
    lancamentosPorAno: { '2025': kzeroLanc2025, '2026': kzeroLancQ1_2026 },
  },
  'kzerosystem': {
    config: kzsConfig,
    planoContas: kzsPlano,
    regrasClassif: kzsRegras,
    receitasPgdas: kzsReceitasPgdas,
    dadosReferencia: kzsDadosReferencia,
    aberturaPorAno: { '2026': kzsAbertura2026 },
    lancamentosPorAno: { '2026': kzsLanc2026 },
  },
  'adc-innovation': {
    config: adcConfig,
    planoContas: adcPlano,
    regrasClassif: adcRegras,
    receitasPgdas: adcReceitasPgdas,
    dadosReferencia: adcDadosReferencia,
    aberturaPorAno: { '2026': adcAbertura2026 },
    lancamentosPorAno: { '2025': adcLanc2025, '2026': adcLanc2026 },
  },
  'lousi-veller': {
    config: lousiConfig,
    planoContas: lousiPlano,
    regrasClassif: lousiRegras,
    receitasPgdas: lousiReceitasPgdas,
    dadosReferencia: lousiDadosReferencia,
    aberturaPorAno: { '2026': lousiAbertura2026 },
    lancamentosPorAno: { '2026': lousiLanc2026 },
  },
};

const EMPTY_DATASET = {
  config: null,
  planoContas: [],
  regrasClassif: [],
  receitasPgdas: {},
  dadosReferencia: {},
  aberturaPorAno: {},
  lancamentosPorAno: {},
};

/**
 * Retorna todo o dataset contábil da empresa ativa.
 *
 * Uso típico:
 *   const { config, planoContas, regrasClassif, dadosReferencia,
 *           receitasPgdas, lancamentosContabeisBase } = useEmpresaContabil();
 *
 * `lancamentosContabeisBase` é a concatenação de todos os lançamentos históricos
 * + abertura de todos os exercícios (sem encerramentos automáticos — esses ficam
 * a cargo do componente de Relatórios, que já sabe como reconstruí-los).
 */
export function useEmpresaContabil() {
  const { empresaAtiva } = useEmpresa();
  const slug = empresaAtiva?.slug;

  const dataset = REGISTRY[slug] || EMPTY_DATASET;

  const lancamentosContabeisBase = useMemo(() => {
    const anos = Object.keys(dataset.lancamentosPorAno || {}).sort();
    const lancs = anos.flatMap(ano => dataset.lancamentosPorAno[ano] || []);
    const anosAbertura = Object.keys(dataset.aberturaPorAno || {}).sort();
    const aberturas = anosAbertura.flatMap(ano => dataset.aberturaPorAno[ano] || []);
    // Ordem: [lançamentos históricos anteriores] + [aberturas] + [lançamentos do ano da abertura].
    // Para manter paridade com Fase 1 (lanc2025 + ABERTURA_2026 + lancQ1_2026), faço uma ordenação
    // estável por data mas preservando a posição original quando datas iguais.
    const comIdx = [...lancs, ...aberturas].map((l, i) => ({ l, i }));
    comIdx.sort((a, b) => {
      const da = a.l.data || '';
      const db = b.l.data || '';
      if (da !== db) return da.localeCompare(db);
      return a.i - b.i;
    });
    return comIdx.map(x => x.l);
  }, [dataset]);

  return {
    slug,
    config: dataset.config,
    planoContas: dataset.planoContas,
    regrasClassif: dataset.regrasClassif,
    receitasPgdas: dataset.receitasPgdas,
    dadosReferencia: dataset.dadosReferencia,
    aberturaPorAno: dataset.aberturaPorAno,
    lancamentosPorAno: dataset.lancamentosPorAno,
    lancamentosContabeisBase,
  };
}

export default useEmpresaContabil;
